import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { jsx } from 'react/jsx-runtime';
import type { CartProduct } from '../../../types/cart';
import ProductCard from '../../../components/product-card';
import Spinner from '../../../components/spinner';
import type { RootState } from '../../../redux/store';
import DEFAULT_IMAGE from '../../../assets/images/default-product.jpg';
import PromoCodeForm from './promo-code-form';
import PaymentBreakdown from './payment-breakdown';

interface CurrentPosition {
  latitude: number;
  longitude: number;
}
interface OrderSummaryProps {
  handleQuantityChange: (id: number, increment: boolean) => void;
  handleRemove: (id: number) => void;
  confirmOrder: () => void;
  loading: boolean;
  disableSubmit: boolean;
  currentPosition: CurrentPosition | null;
}

export default function OrderSummary({
  handleQuantityChange,
  handleRemove,
  confirmOrder,
  loading,
  disableSubmit,
  currentPosition,
}: OrderSummaryProps) {
  const { products } = useSelector((state: RootState) => state.cart);
  const { t } = useTranslation();

  const productItems = products?.map((product: CartProduct) => jsx(
    'li',
    {
      className: 'px-4 py-2 sm:px-6',
      children: jsx(ProductCard, {
        data: product,
        logo: product.logo?.url || DEFAULT_IMAGE,
        handleIncrement: () => handleQuantityChange(product.id, true),
        handleDecrement: () => handleQuantityChange(product.id, false),
        handleRemove: () => handleRemove(product.id),
      }),
    },
    product.id,
  ));

  return jsx('div', {
    className: 'lg:order-2',
    children: jsx('div', {
      className: 'mt-4 rounded-lg border border-gray-200 bg-white shadow-sm',
      children: [
        jsx('div', {
          className: 'px-4 py-6 sm:px-6 border-b border-gray-200',
          children: jsx('h2', {
            className: 'text-lg font-medium text-black',
            children: t('checkout.orderSummary.title'),
          }),
        }),
        jsx('h3', { className: 'sr-only', children: 'Items in your cart' }),
        jsx('ul', { className: 'divide-y divide-gray-200', children: productItems }),
        jsx('div', {
          className: 'border-t border-gray-200 px-4 py-6 sm:px-6 text-sm',
          children: jsx(PromoCodeForm, { currentPosition }),
        }),
        jsx('dl', {
          className: 'border-t border-gray-200 px-4 py-6 sm:px-6 text-sm',
          children: jsx(PaymentBreakdown, {}),
        }),
        jsx('div', {
          className: 'border-t border-gray-200 px-4 py-6 sm:px-6',
          children: jsx('button', {
            type: 'button',
            className: 'w-full rounded-md bg-primary px-3 py-2 text-base font-medium leading-6 text-white shadow-sm hover:bg-lime-600 disabled:bg-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-600',
            onClick: confirmOrder,
            disabled: disableSubmit,
            children: loading ? jsx(Spinner, {}) : t('checkout.confirmOrder'),
          }),
        }),
      ],
    }),
  });
}
